(function AdzukiTos($, Adzuki)
{
	const SCROLL_THRESHOLD_PX        = 16;
	const CAPTIONS_TRACK_LABEL       = 'English';
	const VIDEO_COMPLETE_TOLERANCE_S = 0.5;
	const SEEK_TIP_MS                = 2600;

	let instanceCount = 0;

	function formatTime(seconds) {
		if (!Number.isFinite(seconds) || seconds < 0) {
			return '';
		}

		const whole = Math.floor(seconds);
		const secs  = whole % 60;

		return `${Math.floor(whole / 60)}:${secs < 10 ? '0' : ''}${secs}`;
	}

	const component = {
		emits: ['accepted'],
		props: {
			title: {
				type: String,
			},
			termsConditions: {
				type    : String,
				default : '',
				required: true,
			},
			videoUrl: {
				type   : String,
				default: '',
			},
			captionsUrl: {
				type   : String,
				default: '',
			},
		},
		data() {
			instanceCount += 1;

			return {
				accepted     : false,
				videoWatched : false,
				videoError   : false,
				scrolledToEnd: false,
				maxVideoTime : 0,
				videoDuration: 0,
				seekTip      : false,
				saving       : false,
				checkboxId   : `adzuki-tos-accept-${instanceCount}`,
				whyId        : `adzuki-tos-why-${instanceCount}`,
				debugSnapshot: null,
			};
		},
		computed: {
			videoRequired() {
				return Boolean(this.videoUrl);
			},
			contentReady() {
				return Boolean(this.termsConditions);
			},
			debugEnabled() {
				return new URLSearchParams(window.location.search).get('tosdebug') === '1';
			},
			requirementsMet() {
				const videoOk  = !this.videoRequired || this.videoWatched;
				const scrollOk = this.scrolledToEnd;

				return videoOk && scrollOk;
			},
			canSubmit() {
				return this.requirementsMet && this.accepted && !this.saving;
			},
			logoUrl() {
				return `${this.$baseUri}/adzuki/images/maxx_media_white.png`;
			},
			requirements() {
				const rows = [];

				if (this.videoRequired) {
					let status = 'You must watch the entire video';
					let state  = '';

					if (this.videoWatched) {
						status = 'Video completed';
						state  = 'is-done';
					} else if (this.videoError) {
						status = 'The video couldn\u2019t load';
						state  = 'is-error';
					} else if (this.maxVideoTime > 0) {
						status = `Watching \u00b7 ${formatTime(this.maxVideoTime)} of ${formatTime(this.videoDuration)}`;
						state  = 'is-active';
					} else if (this.videoDuration > 0) {
						status += ` \u00b7 ${formatTime(this.videoDuration)}`;
					}

					rows.push({
						key  : 'video',
						label: 'Watch the video',
						status,
						state,
					});
				}

				rows.push({
					key   : 'terms',
					label : 'Read the terms',
					status: this.scrolledToEnd ? 'Terms and Conditions have been read' : 'You must read and scroll to the end',
					state : this.scrolledToEnd ? 'is-done' : '',
				});

				return rows;
			},
			videoPending() {
				return this.videoRequired && !this.videoWatched;
			},
			whyLocked() {
				if (this.accepted) {
					return 'Thanks. Press Accept Terms to continue.';
				}

				if (this.requirementsMet) {
					return 'Tick the box to accept.';
				}

				if (this.videoPending && !this.scrolledToEnd) {
					return 'Watch the whole video and scroll to the end of the terms to unlock this.';
				}

				if (this.videoPending) {
					return 'Finish the video to unlock this.';
				}

				return 'Scroll to the end of the terms to unlock this.';
			},
		},
		watch: {
			termsConditions() {
				this.scrolledToEnd = false;
				this.$nextTick(() => this.checkScrollPosition());
			},
			videoUrl() {
				this.resetVideoProgress();
			},
			captionsUrl() {
				this.$nextTick(() => this.enableDefaultCaptions());
			},
			requirementsMet(isMet) {
				if (!isMet) {
					this.accepted = false;
				}
			},
		},
		mounted() {
			this.$nextTick(() => {
				this.checkScrollPosition();
				this.updateDebugSnapshot();
			});
		},
		updated() {
			this.$nextTick(() => this.checkScrollPosition());
		},
		beforeUnmount() {
			clearTimeout(this.seekTipTimer);
		},
		methods: {
			checkScrollPosition() {
				if (!this.contentReady) {
					this.scrolledToEnd = false;
					return;
				}

				const content = this.$refs.tosContent;

				if (!content) {
					return;
				}

				const scrollable = content.scrollHeight > content.clientHeight + 1;

				if (!scrollable) {
					this.scrolledToEnd = true;
					return;
				}

				const atBottom = content.scrollTop + content.clientHeight >= content.scrollHeight - SCROLL_THRESHOLD_PX;

				if (atBottom) {
					this.scrolledToEnd = true;
				}
			},
			onContentScroll() {
				this.checkScrollPosition();
				this.updateDebugSnapshot();
			},
			resetVideoProgress() {
				this.videoWatched = false;
				this.videoError    = false;
				this.maxVideoTime  = 0;
				this.videoDuration = 0;
				this.seekTip       = false;

				const video = this.$refs.tosVideo;

				if (video) {
					video.pause();
					video.currentTime = 0;
				}
			},
			onVideoTimeUpdate() {
				const video = this.$refs.tosVideo;

				if (!video) {
					return;
				}

				if (!video.seeking && video.currentTime > this.maxVideoTime) {
					this.maxVideoTime = video.currentTime;
				}

				this.checkVideoComplete();
				this.updateDebugSnapshot();
			},
			onVideoSeeking() {
				const video = this.$refs.tosVideo;

				if (!video || this.videoWatched) {
					return;
				}

				if (video.currentTime > this.maxVideoTime + 0.25) {
					video.currentTime = this.maxVideoTime;
					this.showSeekTip();
				}
			},
			showSeekTip() {
				this.seekTip = true;

				clearTimeout(this.seekTipTimer);
				this.seekTipTimer = setTimeout(() => {
					this.seekTip = false;
				}, SEEK_TIP_MS);
			},
			retryVideo() {
				const video = this.$refs.tosVideo;

				this.videoError = false;

				if (video) {
					video.load();
				}
			},
			checkVideoComplete() {
				const video = this.$refs.tosVideo;

				if (!video || this.videoWatched || video.seeking) {
					return;
				}

				const duration = video.duration;

				if (!Number.isFinite(duration) || duration <= 0) {
					return;
				}

				if (this.maxVideoTime >= duration - VIDEO_COMPLETE_TOLERANCE_S) {
					this.markVideoWatched();
				}
			},
			markVideoWatched() {
				const video = this.$refs.tosVideo;

				this.videoWatched = true;
				this.maxVideoTime = video && Number.isFinite(video.duration) ? video.duration : this.maxVideoTime;

				this.updateDebugSnapshot();
			},
			onVideoEnded() {
				this.markVideoWatched();
			},
			onVideoPause() {
				this.checkVideoComplete();
				this.updateDebugSnapshot();
			},
			onVideoError() {
				const video = this.$refs.tosVideo;

				this.videoError = true;

				console.error(
					'Error loading Terms and Conditions video:',
					video?.error?.code,
					video?.currentSrc || this.videoUrl
				);

				this.updateDebugSnapshot();
			},
			onCaptionsError() {
				console.error('Error loading Terms and Conditions captions:', this.captionsUrl);
			},
			onVideoLoadedMetadata() {
				const video = this.$refs.tosVideo;

				this.videoError    = false;
				this.videoDuration = video && Number.isFinite(video.duration) ? video.duration : 0;

				this.$nextTick(() => {
					this.enableDefaultCaptions();
					this.updateDebugSnapshot();
				});
			},
			updateDebugSnapshot() {
				if (!this.debugEnabled) {
					return;
				}

				const video = this.$refs.tosVideo;

				this.debugSnapshot = {
					videoWatched : this.videoWatched,
					videoError   : this.videoError,
					scrolledToEnd: this.scrolledToEnd,
					contentReady : this.contentReady,
					maxVideoTime : this.maxVideoTime,
					currentTime  : video ? video.currentTime : null,
					duration     : video ? video.duration : null,
					readyState   : video ? video.readyState : null,
					networkState : video ? video.networkState : null,
					playbackRate : video ? video.playbackRate : null,
				};
			},
			enableDefaultCaptions() {
				const video = this.$refs.tosVideo;

				if (!video?.textTracks?.length) {
					return;
				}

				const captionTracks = Array.from(video.textTracks).filter(
					(track) => track.kind === 'captions' || track.kind === 'subtitles'
				);

				if (!captionTracks.length) {
					return;
				}

				let trackToShow = null;

				if (this.captionsUrl) {
					trackToShow = captionTracks.find((track) => track.label === CAPTIONS_TRACK_LABEL) ||
						captionTracks[captionTracks.length - 1];
				} else {
					trackToShow = captionTracks[0];
				}

				captionTracks.forEach((track) => {
					track.mode = track === trackToShow ? 'showing' : 'hidden';
				});
			},
			acceptTerms() {
				if (!this.canSubmit) {
					return;
				}

				const url = `${this.$baseUri}/Media/acceptTos/${this.accepted}`;

				this.saving = true;

				fetch(url)
					.then(response => {
						if (!response.ok) {
							throw new Error(`Error Accepting Terms and conditions: ${response.statusText}`);
						}
						return response.json();
					})
					.then(data => {
						if (!data || !data.success) {
							throw new Error('Error Accepting Terms and Conditions: no success flag');
						}

						this.$emit('accepted');
					})
					.catch(error => {
						console.error('Error Accepting Terms and Conditions:', error);

						if (typeof Adzuki.StatusMessage === 'function') {
							Adzuki.StatusMessage('error', 'Your acceptance could not be saved. Please try again.', 'Error');
						}

						return null;
					})
					.finally(() => {
						this.saving = false;
					});
			},
		},
		template: `
			<div id="adzuki-tos-container" class="col">
				<div class="adzuki-tos-stage">
					<div v-if="!contentReady" class="adzuki-tos-card adzuki-tos-loading" role="status">
						<span class="spinner-border text-primary" aria-hidden="true"></span>
						<span>Loading the Maxx Media terms\u2026</span>
					</div>
					<section v-else class="adzuki-tos-card" aria-labelledby="adzuki-tos-heading">
						<div class="adzuki-tos-header">
							<span class="adzuki-tos-mark"><img :src="logoUrl" alt=""></span>
							<div>
								<h1 id="adzuki-tos-heading" class="adzuki-tos-title">Before you use Maxx Media</h1>
								<p class="adzuki-tos-intro">
									Home Office asks every advisor to review how photos, videos and documents can be used.
									{{ videoRequired ? 'Watch a short video, then read and accept the terms.' : 'Read and accept the terms.' }}
									You only do this once.
								</p>
							</div>
						</div>

						<div class="adzuki-tos-requirements" role="list" aria-label="What\u2019s left before you can accept">
							<div
								v-for="row in requirements"
								:key="row.key"
								class="adzuki-tos-requirement"
								:class="row.state"
								role="listitem"
							>
								<span class="adzuki-tos-requirement-mark" aria-hidden="true">
									<i v-if="row.state === 'is-done'" class="fas fa-check"></i>
									<i v-else-if="row.state === 'is-error'" class="fas fa-exclamation"></i>
									<template v-else-if="row.state === 'is-active'">\u2026</template>
									<template v-else>\u00b7</template>
								</span>
								<span class="adzuki-tos-requirement-text">
									<span class="adzuki-tos-requirement-label">{{ row.label }}</span>
									<span class="adzuki-tos-requirement-status">{{ row.status }}</span>
								</span>
							</div>
						</div>

						<div v-if="videoRequired" class="adzuki-tos-section tos-video-container">
							<div v-if="videoError" class="adzuki-tos-video-error" role="alert">
								<span>The video couldn\u2019t load, so you can\u2019t finish yet. Check your connection and try again. If it keeps happening, contact Home Office support.</span>
								<button type="button" class="btn btn-default" @click="retryVideo">Try again</button>
							</div>
							<video
								v-show="!videoError"
								crossorigin="anonymous"
								ref="tosVideo"
								class="tos-video w-100"
								controls
								playsinline
								preload="metadata"
								:src="videoUrl"
								@loadedmetadata="onVideoLoadedMetadata"
								@timeupdate="onVideoTimeUpdate"
								@seeking="onVideoSeeking"
								@pause="onVideoPause"
								@ended="onVideoEnded"
								@error="onVideoError"
							>
								<track
									v-if="captionsUrl"
									kind="captions"
									:src="captionsUrl"
									srclang="en"
									label="English"
									default
									@error="onCaptionsError"
								>
							</video>
							<p v-if="seekTip" class="adzuki-tos-seek-tip" role="status">You can go back in the video, but not skip ahead of what you\u2019ve watched.</p>
						</div>

						<div class="adzuki-tos-section">
							<div class="adzuki-tos-terms-heading">
								<h2>{{ title || 'Media Submission Terms and Conditions' }}</h2>
								<span :class="scrolledToEnd ? 'is-done' : 'is-todo'">
									<template v-if="scrolledToEnd"><i class="fas fa-check"></i> Read to the end</template>
									<template v-else>Scroll to the end</template>
								</span>
							</div>
							<div
								ref="tosContent"
								class="adzuki-tos-content row"
								tabindex="0"
								role="region"
								aria-label="Terms and Conditions. Scroll to the end to continue."
								v-html="termsConditions"
								@scroll="onContentScroll"
							></div>
						</div>

						<div class="adzuki-tos-footer">
							<div class="adzuki-tos-agree">
								<div class="form-check mb-0">
									<input
										:id="checkboxId"
										class="form-check-input adzuki-tos-accept"
										type="checkbox"
										v-model="accepted"
										:disabled="!requirementsMet || saving"
										:aria-describedby="whyId"
									>
									<label
										class="form-check-label"
										:for="checkboxId"
										:class="{ 'is-locked': !requirementsMet }"
									>
										I have read and agree to all Media Submission Terms and Conditions.
									</label>
								</div>
								<button
									type="button"
									class="btn btn-primary adzuki-tos-submit"
									:disabled="!canSubmit"
									@click="acceptTerms"
								>
									{{ saving ? 'Saving\u2026' : 'Accept Terms' }}
								</button>
							</div>
							<p :id="whyId" class="adzuki-tos-why">{{ whyLocked }}</p>
						</div>
					</section>
					<pre v-if="debugEnabled" class="adzuki-tos-debug" aria-label="Debug information">{{ debugSnapshot }}</pre>
				</div>
			</div>
		`,
	};


	Adzuki.VueComponents.AdzukiTos= component;
}(jQuery, window.Adzuki || {}));
